import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import { openSnackbar as openGlobalSnackbar } from "../../../reducers/snackbar"
import { canUsePowerDialer, isConcierge } from "../../users/accountRole"
import { isActiveSubscriptionStatus } from "../../users/billing"
import { openSaveSessionNotesModal } from "../../users/openSaveSessionNotesModal"
import { selectProfile, selectSubscription } from "../../users/reducer"
import {
  TrialFinishedRoadblockDialogData,
  useTrialFinishedDialogUtils,
} from "../components/TrialFinishedRoadblockDialog"
import { openConciergeGreetingRequiredDialog } from "../reducer"
import { selectCRMSettings } from "../reducer"
import { SETTINGS_TABS, createSettingsTabURL } from "../settings"
import { useGlobalCall } from "./GlobalCallProvider"
import PowerDialerBar from "./PowerDialerBar"
import PowerDialerSummaryDialog from "./PowerDialerSummaryDialog"
import { amdAdvanceExtra } from "./amd"
import { getOutboundCallGateState } from "./conciergeCallingGate"
import {
  amdPayloadFromSession,
  contactFromDialerItem,
  dialerSessionErrorMessage,
} from "./powerDialer"
import { useStartLeadCall } from "./useStartLeadCall"

const PowerDialerContext = createContext(null)

export function usePowerDialer() {
  const context = useContext(PowerDialerContext)
  if (!context) {
    throw new Error("usePowerDialer must be used within PowerDialerProvider")
  }
  return context
}

export function PowerDialerProvider({ children }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const axios = useAuthorizedAxiosClient()
  const { isBusy, abortCall, startCall } = useGlobalCall()
  const { requestCall } = useStartLeadCall()
  const crmSettings = useSelector(selectCRMSettings)
  const profile = useSelector(selectProfile)
  const subscription = useSelector(selectSubscription)
  const { runOnlyIfSubscribed } = useTrialFinishedDialogUtils()

  const [session, setSession] = useState(null)
  const [isStarting, setIsStarting] = useState(false)
  const [isAdvancing, setIsAdvancing] = useState(false)
  const [wrapUpSecondsLeft, setWrapUpSecondsLeft] = useState(null)
  const [summary, setSummary] = useState(null)
  const [isOpeningDailyReport, setIsOpeningDailyReport] = useState(false)

  const sessionRef = useRef(null)
  const pausedRef = useRef(false)
  const wrapUpTimerRef = useRef(null)
  const wrapUpTickRef = useRef(null)
  const isBusyRef = useRef(isBusy)
  const requestCallRef = useRef(requestCall)
  const startCallRef = useRef(startCall)
  isBusyRef.current = isBusy
  requestCallRef.current = requestCall
  startCallRef.current = startCall

  const notify = useCallback(
    (message) => {
      dispatch(openGlobalSnackbar({ message }))
    },
    [dispatch]
  )

  const clearWrapUp = useCallback(() => {
    if (wrapUpTimerRef.current) {
      window.clearTimeout(wrapUpTimerRef.current)
      wrapUpTimerRef.current = null
    }
    if (wrapUpTickRef.current) {
      window.clearInterval(wrapUpTickRef.current)
      wrapUpTickRef.current = null
    }
    setWrapUpSecondsLeft(null)
  }, [])

  const applySession = useCallback((nextSession) => {
    sessionRef.current = nextSession
    pausedRef.current = nextSession?.status === "paused"
    setSession(nextSession)
  }, [])

  const finishSession = useCallback(
    (snapshot) => {
      clearWrapUp()
      abortCall()
      applySession(null)
      if (snapshot?.queue_count) {
        setSummary(snapshot)
      }
    },
    [abortCall, applySession, clearWrapUp]
  )

  const assertCanStartCalls = useCallback(() => {
    if (!canUsePowerDialer(profile)) {
      notify(
        "Power Dialer is available to the account owner and Concierge users."
      )
      return false
    }
    if (!isActiveSubscriptionStatus(subscription?.status)) {
      notify("Subscribe to a paid plan to use Power Dialer.")
      return false
    }
    if (subscription?.trialFinished) {
      runOnlyIfSubscribed(() => {},
      new TrialFinishedRoadblockDialogData("Making Calls", "Subscribe to make phone calls to your leads"))
      return false
    }
    switch (getOutboundCallGateState(crmSettings, profile)) {
      case "concierge_no_phone":
        navigate(createSettingsTabURL(SETTINGS_TABS.phoneSms))
        return false
      case "concierge_no_greeting":
        dispatch(openConciergeGreetingRequiredDialog())
        return false
      case "owner_no_phone":
        navigate(
          createSettingsTabURL(SETTINGS_TABS.phoneSms, { focus: "phone" })
        )
        return false
      default:
        return true
    }
  }, [
    crmSettings,
    dispatch,
    navigate,
    notify,
    profile,
    runOnlyIfSubscribed,
    subscription,
  ])

  const dialCurrent = useCallback(
    (nextSession) => {
      const current = nextSession || sessionRef.current
      if (!current || current.status !== "active" || pausedRef.current) {
        return
      }
      const contact = contactFromDialerItem(current.current_item, current)
      if (!contact?.phone) {
        finishSession(sessionRef.current)
        return
      }
      if (isBusyRef.current) {
        return
      }
      // Dock the call log beside the dialer sidebar so the list stays readable.
      const callOptions = {
        logInSidePanel: true,
        onAttemptFinished: (updatedCall, extra) => {
          handleAttemptFinishedRef.current?.(updatedCall, extra)
        },
      }
      if (contact.isLead === false) {
        startCallRef.current(contact, callOptions)
        return
      }
      const started = requestCallRef.current(contact, callOptions)
      if (started === false) {
        notify("Could not start the next call.")
      }
    },
    [finishSession, notify]
  )

  const scheduleWrapUpDial = useCallback(
    (nextSession) => {
      if (!nextSession?.current_item || nextSession.status !== "active") {
        return
      }
      const seconds = Math.max(1, nextSession.wrap_up_seconds || 3)
      clearWrapUp()
      setWrapUpSecondsLeft(seconds)
      wrapUpTickRef.current = window.setInterval(() => {
        setWrapUpSecondsLeft((current) => {
          if (current == null || current <= 1) return 0
          return current - 1
        })
      }, 1000)
      wrapUpTimerRef.current = window.setTimeout(() => {
        clearWrapUp()
        if (!pausedRef.current) {
          dialCurrent(sessionRef.current)
        }
      }, seconds * 1000)
    },
    [clearWrapUp, dialCurrent]
  )

  const patchStatus = useCallback(
    (status) => {
      const current = sessionRef.current
      if (!current?.id) return Promise.resolve()
      return axios
        .patch(`/crm/api/dialer/sessions/${current.id}/`, { status })
        .then(({ data }) => {
          applySession(data)
          return data
        })
    },
    [applySession, axios]
  )

  const advance = useCallback(
    (action, extra = {}) => {
      const current = sessionRef.current
      if (!current?.id) return Promise.resolve(null)
      setIsAdvancing(true)
      const body = { action }
      if (extra.disposition) body.disposition = extra.disposition
      if (extra.amd_result) body.amd_result = extra.amd_result
      if (extra.amd_action) body.amd_action = extra.amd_action
      return axios
        .post(`/crm/api/dialer/sessions/${current.id}/advance/`, body)
        .then(({ data }) => {
          if (data.status === "ended" || !data.current_item) {
            finishSession(data)
            return null
          }
          applySession(data)
          return data
        })
        .catch((error) => {
          notify(
            parseApiErrorMessage(
              error?.response?.data,
              "Could not move to the next lead."
            )
          )
          return null
        })
        .finally(() => setIsAdvancing(false))
    },
    [applySession, axios, finishSession, notify]
  )

  const handleAttemptFinished = useCallback(
    (updatedCall, extra = {}) => {
      if (!sessionRef.current) return
      advance(updatedCall ? "completed" : "skipped", {
        disposition: updatedCall?.disposition,
        ...amdAdvanceExtra(extra?.amdStatus),
      }).then((nextSession) => {
        if (!nextSession || pausedRef.current) return
        scheduleWrapUpDial(nextSession)
      })
    },
    [advance, scheduleWrapUpDial]
  )
  const handleAttemptFinishedRef = useRef(handleAttemptFinished)
  handleAttemptFinishedRef.current = handleAttemptFinished

  const startSession = useCallback(
    ({ source, filters, ordering, selectedIds, name, amd }) => {
      if (!assertCanStartCalls()) return Promise.resolve(false)
      const sessionName = String(name || "").trim()
      if (!sessionName) {
        notify("Enter a name for this Power Dialer session.")
        return Promise.resolve(false)
      }
      setIsStarting(true)
      clearWrapUp()
      return axios
        .post("/crm/api/dialer/sessions/", {
          name: sessionName,
          source,
          filters: filters || [],
          ordering: ordering || "",
          selected_ids: selectedIds || [],
          ...(amd ? { amd } : {}),
        })
        .then(({ data }) => {
          applySession(data)
          const isCandidates = data.source === "candidates"
          const itemNoun = isCandidates ? "candidate" : "lead"
          notify(
            data.queue_capped
              ? `Dialing the first ${data.queue_count} matching ${itemNoun}s.`
              : `Dialing ${data.queue_count} ${itemNoun}${
                  data.queue_count === 1 ? "" : "s"
                }.`
          )
          dialCurrent(data)
          return true
        })
        .catch((error) => {
          notify(dialerSessionErrorMessage(error))
          return false
        })
        .finally(() => setIsStarting(false))
    },
    [applySession, assertCanStartCalls, axios, clearWrapUp, dialCurrent, notify]
  )

  const pauseSession = useCallback(() => {
    clearWrapUp()
    pausedRef.current = true
    patchStatus("paused")
  }, [clearWrapUp, patchStatus])

  const resumeSession = useCallback(() => {
    pausedRef.current = false
    patchStatus("active").then((data) => {
      if (!data) return
      if (!isBusyRef.current) {
        dialCurrent(data)
      }
    })
  }, [dialCurrent, patchStatus])

  const skipCurrent = useCallback(() => {
    if (isBusyRef.current) {
      notify("Finish or end the current call before skipping.")
      return
    }
    clearWrapUp()
    advance("skipped").then((nextSession) => {
      if (!nextSession || pausedRef.current) return
      dialCurrent(nextSession)
    })
  }, [advance, clearWrapUp, dialCurrent, notify])

  const endSession = useCallback(() => {
    const current = sessionRef.current
    if (!current?.id) {
      finishSession(current)
      return
    }
    axios
      .patch(`/crm/api/dialer/sessions/${current.id}/`, { status: "ended" })
      .then(({ data }) => finishSession(data))
      .catch(() => finishSession(current))
  }, [axios, finishSession])

  useEffect(() => {
    return () => clearWrapUp()
  }, [clearWrapUp])

  const handleOpenDailyReports = useCallback(() => {
    navigate(createSettingsTabURL(SETTINGS_TABS.session))
  }, [navigate])

  const handleOpenDailyReport = useCallback(async () => {
    setIsOpeningDailyReport(true)
    try {
      const { data } = await axios.get(
        "/clients/api/concierge/daily-activity/report-form/"
      )
      if (data?.report_date) {
        await openSaveSessionNotesModal({
          axios,
          reportDate: data.report_date,
        })
      }
    } finally {
      setIsOpeningDailyReport(false)
    }
  }, [axios])

  const value = useMemo(
    () => ({
      session,
      hasOpenSession: Boolean(session),
      isStarting,
      startSession,
      pauseSession,
      resumeSession,
      skipCurrent,
      endSession,
    }),
    [
      endSession,
      isStarting,
      pauseSession,
      resumeSession,
      session,
      skipCurrent,
      startSession,
    ]
  )

  return (
    <PowerDialerContext.Provider value={value}>
      {children}
      <PowerDialerBar
        session={session}
        wrapUpSecondsLeft={wrapUpSecondsLeft}
        isBusy={isBusy}
        isAdvancing={isAdvancing}
        showDailyReports={isConcierge(profile)}
        onDailyReports={handleOpenDailyReports}
        onSkip={skipCurrent}
        onEnd={endSession}
      />
      <PowerDialerSummaryDialog
        open={Boolean(summary)}
        session={summary}
        isRetrying={isStarting}
        showDailyReport={isConcierge(profile)}
        isOpeningDailyReport={isOpeningDailyReport}
        onOpenDailyReport={handleOpenDailyReport}
        onClose={() => setSummary(null)}
        onRetry={(selectedIds, sessionName) => {
          if (!summary) return
          const snapshot = summary
          startSession({
            source: snapshot.source,
            filters: snapshot.filters || [],
            ordering: snapshot.ordering || "",
            selectedIds,
            name: sessionName,
            amd: amdPayloadFromSession(snapshot),
          }).then((started) => {
            if (started) setSummary(null)
          })
        }}
      />
    </PowerDialerContext.Provider>
  )
}
